import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import GenericDataTable from '../components/GenericDataTable';
import KPICard from '../components/KPICard';
import companyDataService from '../services/companyDataService';
import exportService from '../services/exportService';
import pdfExportService from '../services/pdfExportService';

const ProductsList = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Estados para filtros
  const [filters, setFilters] = useState({
    filial: '',
    dispositivo: '',
    minConsultas: '',
    maxConsultas: '',
    busca: ''
  });

  // Estados para estatísticas
  const [stats, setStats] = useState({
    totalProdutos: 0,
    totalConsultas: 0,
    mediaConsultas: 0,
    produtoMaisConsultado: null
  });

  const [availableFiliais, setAvailableFiliais] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [availableDispositivos, setAvailableDispositivos] = useState([]);

  useEffect(() => {
    loadProductsData();
  }, []);

  const loadProductsData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await companyDataService.loadData();
      const allProducts = companyDataService.getAllData();
      
      setProducts(allProducts);
      setFilteredProducts(allProducts);
      calculateStats(allProducts);
      
      // Carregar opções para filtros
      setAvailableFiliais(companyDataService.getUniqueFiliais());
      setAvailableDispositivos(companyDataService.getUniqueDispositivos());
      
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      setError('Erro ao carregar dados dos produtos. Verifique se o arquivo JSON está disponível.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = (productsList) => {
    if (productsList.length === 0) {
      setStats({
        totalProdutos: 0,
        totalConsultas: 0,
        mediaConsultas: 0,
        produtoMaisConsultado: null
      });
      return;
    }

    const totalConsultas = productsList.reduce((sum, product) => sum + (product.num_consultas || 0), 0);
    const mediaConsultas = totalConsultas / productsList.length;
    const produtoMaisConsultado = productsList.reduce((max, product) => 
      (product.num_consultas || 0) > (max.num_consultas || 0) ? product : max
    );

    setStats({
      totalProdutos: productsList.length,
      totalConsultas,
      mediaConsultas,
      produtoMaisConsultado
    });
  };

  const applyFilters = () => {
    let filtered = products;

    // Filtro por filial
    if (filters.filial && filters.filial !== '') {
      filtered = filtered.filter(product => 
        product.filial && product.filial.toString() === filters.filial
      );
    }

    // Filtro por dispositivo
    if (filters.dispositivo && filters.dispositivo !== '') {
      filtered = filtered.filter(product => 
        product.apelido_dispositivo && 
        product.apelido_dispositivo.toLowerCase().includes(filters.dispositivo.toLowerCase())
      );
    }

    // Filtro por número mínimo de consultas
    if (filters.minConsultas && filters.minConsultas > 0) {
      filtered = filtered.filter(product => 
        (product.num_consultas || 0) >= parseInt(filters.minConsultas)
      );
    }

    // Filtro por número máximo de consultas
    if (filters.maxConsultas && filters.maxConsultas > 0) {
      filtered = filtered.filter(product => 
        (product.num_consultas || 0) <= parseInt(filters.maxConsultas)
      );
    }

    // Busca por produto
    if (filters.busca && filters.busca.trim() !== '') {
      const searchTerm = filters.busca.toLowerCase();
      filtered = filtered.filter(product => {
        const description = (product.description || '').toLowerCase();
        const codbar = (product.codbar || '').toString().toLowerCase();
        
        return description.includes(searchTerm) || codbar.includes(searchTerm);
      });
    }

    setFilteredProducts(filtered);
    calculateStats(filtered);
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    applyFilters();
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      filial: '',
      dispositivo: '',
      minConsultas: '',
      maxConsultas: '',
      busca: ''
    };
    setFilters(resetFilters);
    setFilteredProducts(products);
    calculateStats(products);
    setShowFilters(false);
  };

  const handleExportProducts = async () => {
    try {
      const dataToExport = filteredProducts.map(product => ({
        'Código de Barras': product.codbar,
        'Descrição': product.description,
        'Dispositivo': product.apelido_dispositivo,
        'Consultas': product.num_consultas,
        'Filial': product.filial
      }));
      
      await exportService.exportToExcel(dataToExport, `produtos_empresa_${Date.now()}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar produtos:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const dataToExport = {
        totalProdutos: stats.totalProdutos,
        totalConsultas: stats.totalConsultas,
        mediaConsultas: stats.mediaConsultas.toFixed(1),
        produtoMaisConsultado: stats.produtoMaisConsultado,
        products: filteredProducts
      };
      
      await pdfExportService.exportProductsReport(dataToExport);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
    }
  };

  const handleUpdateData = async () => {
    try {
      await companyDataService.updateFromApi();
      await loadProductsData();
      return { success: true, message: 'Dados atualizados com sucesso!' };
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      throw error;
    }
  };

  // Colunas para a tabela de produtos
  const columns = [
    { key: 'codbar', label: 'Código de Barras', sortable: true },
    { key: 'description', label: 'Descrição', sortable: true },
    { key: 'apelido_dispositivo', label: 'Dispositivo', sortable: true },
    { key: 'num_consultas', label: 'Consultas', sortable: true, type: 'number' },
    { key: 'filial', label: 'Filial', sortable: true }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-dark-text mb-2">Carregando Produtos</h3>
          <p className="text-dark-text-secondary">
            Processando dados dos produtos...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="card-dark p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-danger rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-dark-text mb-2">Erro ao Carregar</h3>
          <p className="text-dark-text-secondary mb-4">{error}</p>
          <button
            onClick={loadProductsData}
            className="btn-primary"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-dark-text mb-2">
            Lista de Produtos
          </h1>
          <p className="text-dark-text-secondary">
            Empresa: {companyDataService.companyId}
          </p>
        </div>
         <div className="flex gap-2">
           <button
             onClick={() => setShowFilters(!showFilters)}
             className="btn-secondary flex items-center gap-2"
           >
             <FunnelIcon className="w-5 h-5" />
             Filtros
           </button>
           <button
             onClick={handleExportPDF}
             className="btn-primary flex items-center gap-2"
           >
             <DocumentArrowDownIcon className="w-5 h-5" />
             Exportar PDF
           </button>
           <button
             onClick={handleExportProducts}
             className="btn-secondary flex items-center gap-2"
           >
             <DocumentArrowDownIcon className="w-5 h-5" />
             Exportar Excel
           </button>
         </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Total de Produtos"
          value={companyDataService.formatNumber(stats.totalProdutos)}
          icon={ChartBarIcon}
          color="primary"
        />
        <KPICard
          title="Total de Consultas"
          value={companyDataService.formatNumber(stats.totalConsultas)}
          icon={ChartBarIcon}
          color="success"
        />
        <KPICard
          title="Média de Consultas"
          value={stats.mediaConsultas.toFixed(1)}
          icon={ChartBarIcon}
          color="warning"
        />
        <KPICard
          title="Produto Mais Consultado"
          value={stats.produtoMaisConsultado ? stats.produtoMaisConsultado.num_consultas : 0}
          icon={ChartBarIcon}
          color="info"
          subtitle={stats.produtoMaisConsultado ? 
            stats.produtoMaisConsultado.description?.substring(0, 30) + '...' : 
            'Nenhum produto'
          }
        />
      </div>

      {/* Painel de Filtros */}
      {showFilters && (
        <div className="card-dark p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-dark-text">Filtros Avançados</h3>
            <button
              onClick={() => setShowFilters(false)}
              className="text-dark-text-secondary hover:text-dark-text"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-dark-text-secondary mb-2">
                Filial
              </label>
              <select
                value={filters.filial}
                onChange={(e) => handleFilterChange('filial', e.target.value)}
                className="input-dark w-full"
              >
                <option value="">Todas as filiais</option>
                {availableFiliais.map(filial => (
                  <option key={filial} value={filial}>
                    Filial {filial}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text-secondary mb-2">
                Dispositivo
              </label>
              <input
                type="text"
                value={filters.dispositivo}
                onChange={(e) => handleFilterChange('dispositivo', e.target.value)}
                placeholder="Nome do dispositivo..."
                className="input-dark w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text-secondary mb-2">
                Buscar Produto
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-text-secondary" />
                <input
                  type="text"
                  value={filters.busca}
                  onChange={(e) => handleFilterChange('busca', e.target.value)}
                  placeholder="Código ou descrição..."
                  className="input-dark w-full pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text-secondary mb-2">
                Mín. Consultas
              </label>
              <input
                type="number"
                value={filters.minConsultas}
                onChange={(e) => handleFilterChange('minConsultas', e.target.value)}
                placeholder="Ex: 5"
                className="input-dark w-full"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text-secondary mb-2">
                Máx. Consultas
              </label>
              <input
                type="number"
                value={filters.maxConsultas}
                onChange={(e) => handleFilterChange('maxConsultas', e.target.value)}
                placeholder="Ex: 100"
                className="input-dark w-full"
                min="0"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleApplyFilters}
              className="btn-primary"
            >
              Aplicar Filtros
            </button>
            <button
              onClick={handleResetFilters}
              className="btn-secondary"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      )}

      {/* Tabela de Produtos */}
      <div className="card-dark">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-dark-text">
            Produtos da Empresa
          </h3>
          <p className="text-sm text-dark-text-secondary mt-1">
            {filteredProducts.length !== products.length && 
              `${filteredProducts.length} de ${products.length} produtos (filtrados)`
            }
            {filteredProducts.length === products.length && 
              `${products.length} produtos no total`
            }
          </p>
        </div>
        <GenericDataTable
          data={filteredProducts}
          columns={columns}
          pageSize={25}
          onExport={handleExportProducts}
        />
      </div>
    </div>
  );
};

export default ProductsList;
